import axios from 'axios';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL;

// CSRF token management for admin API
let csrfToken = null;

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    logger.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Create axios instance with HTTP-only cookie support
const adminApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send HTTP-only cookies automatically
});

// Request interceptor to add CSRF token to state-changing requests
adminApi.interceptors.request.use(
  async (config) => {
    // Only add CSRF token to state-changing methods
    const stateChangingMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (stateChangingMethods.includes(config.method.toUpperCase())) {
      // Fetch token if not already cached
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Add CSRF token to header
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle CSRF token expiration
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If we get a 403 CSRF error, try to refresh the token and retry
    if (error.response && error.response.status === 403 && error.response.data.error === 'CSRF token validation failed') {
      // Clear cached token and fetch a new one
      csrfToken = null;
      await fetchCsrfToken();

      // Retry the request with the new token
      const config = error.config;
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
        return adminApi.request(config);
      }
    }
    return Promise.reject(error);
  }
);

// Note: CSRF token is now lazily loaded on first request instead of during module initialization
// This prevents "insecure operation" errors in development environments

const handleApiError = (error) => {
  logger.error('Admin API Error:', error);

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      success: false,
      error: error.response.data.error || 'An error occurred',
      status: error.response.status
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      success: false,
      error: 'No response from server',
      status: 503
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      success: false,
      error: error.message || 'Unknown error',
      status: 500
    };
  }
};

// Admin API functions
export const loginAdmin = async (email, password) => {
  try {
    const response = await adminApi.post('/api/admin/login', { email, password });
    // HTTP-only cookies are set automatically by the backend
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAdminProfile = async () => {
  try {
    const response = await adminApi.get('/api/admin/profile');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateAdminProfile = async (profileData) => {
  try {
    const response = await adminApi.put('/api/admin/profile', profileData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getUsers = async () => {
  try {
    const response = await adminApi.get('/api/admin/users');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getUserById = async (userId) => {
  try {
    const response = await adminApi.get(`/api/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createUser = async (userData) => {
  try {
    const response = await adminApi.post('/api/admin/users', userData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await adminApi.put(`/api/admin/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await adminApi.delete(`/api/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getCustomerDetails = async (userId) => {
  try {
    const response = await adminApi.get(`/api/admin/users/${userId}/details`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAllOrders = async () => {
  try {
    const response = await adminApi.get('/api/admin/orders');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getOrderByOrderNumber = async (orderNumber) => {
  try {
    const response = await adminApi.get(`/api/admin/orders/${orderNumber}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateOrderStatus = async (id, status) => {
  try {
    const response = await adminApi.put(`/api/admin/orders/${id}`, { orderStatus: status });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteOrder = async (id) => {
  try {
    const response = await adminApi.delete(`/api/admin/orders/${id}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getDashboardStats = async () => {
  try {
    const response = await adminApi.get('/api/admin/stats');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAnalytics = async (params) => {
  try {
    const response = await adminApi.get('/api/admin/analytics', { params });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getDashboardData = async (params) => {
  try {
    const response = await adminApi.get('/api/admin/dashboard', { params });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getSettings = async () => {
  try {
    const response = await adminApi.get('/api/settings');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateSettings = async (settingsData) => {
  try {
    // console.log('adminApi.updateSettings sending:', settingsData);
    const response = await adminApi.put('/api/settings', settingsData);
    // console.log('adminApi.updateSettings response:', response.data);
    return response.data;
  } catch (error) {
    // console.error('adminApi.updateSettings error:', error);
    return handleApiError(error);
  }
};

export const awardFreeCoins = async (userId, coinData) => {
  try {
    const response = await adminApi.post(`/api/admin/users/${userId}/award-coins`, coinData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Coupon API calls
export const createCoupon = async (couponData) => {
  try {
    const response = await adminApi.post('/api/admin/coupons', couponData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAllCoupons = async () => {
  try {
    const response = await adminApi.get('/api/admin/coupons');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateCoupon = async (id, couponData) => {
  try {
    const response = await adminApi.put(`/api/admin/coupons/${id}`, couponData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteCoupon = async (id) => {
  try {
    const response = await adminApi.delete(`/api/admin/coupons/${id}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getCouponUsage = async (id) => {
  try {
    const response = await adminApi.get(`/api/admin/coupons/${id}/usage`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Gift Card API calls
export const createGiftCard = async (giftCardData) => {
  try {
    const response = await adminApi.post('/api/admin/giftcards', giftCardData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAllGiftCards = async () => {
  try {
    const response = await adminApi.get('/api/admin/giftcards');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateGiftCard = async (id, giftCardData) => {
  try {
    const response = await adminApi.put(`/api/admin/giftcards/${id}`, giftCardData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteGiftCard = async (id) => {
  try {
    const response = await adminApi.delete(`/api/admin/giftcards/${id}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getGiftCardUsage = async (id) => {
  try {
    const response = await adminApi.get(`/api/admin/giftcards/${id}/usage`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getLogs = async (params) => {
  try {
    const response = await adminApi.get('/api/admin/logs', { params });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getVeeqoStatus = async () => {
  try {
    const response = await adminApi.get('/api/admin/veeqo/status');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const triggerVeeqoSync = async (type) => {
  try {
    const response = await adminApi.post('/api/admin/veeqo/sync', { type });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const logoutAdmin = async () => {
  try {
    await adminApi.post('/api/admin/logout');
    return { success: true };
  } catch (error) {
    // Even if logout fails, we'll return success for UX
    // console.error('Admin logout error:', error);
    return { success: true };
  }
};

export const isAdminAuthenticated = async () => {
  try {
    await adminApi.get('/api/admin/profile');
    return true;
  } catch (error) {
    return false;
  }
};

export const getCurrentAdmin = async () => {
  try {
    const response = await adminApi.get('/api/admin/profile');
    return response.data.admin;
  } catch (error) {
    return null;
  }
};

// Export the adminApi instance as named export
export { adminApi };

// Export all functions as default object
const adminApiFunctions = {
  loginAdmin,
  getAdminProfile,
  updateAdminProfile,
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  getCustomerDetails,
  getAllOrders,
  getOrderByOrderNumber,
  updateOrderStatus,
  deleteOrder,
  getDashboardStats,
  getAnalytics,
  getDashboardData,
  getSettings,
  updateSettings,
  awardFreeCoins,
  createCoupon,
  getAllCoupons,
  updateCoupon,
  deleteCoupon,
  getCouponUsage,
  createGiftCard,
  getAllGiftCards,
  updateGiftCard,
  deleteGiftCard,
  getGiftCardUsage,
  getLogs,
  getVeeqoStatus,
  triggerVeeqoSync,
  logoutAdmin,
  isAdminAuthenticated,
  getCurrentAdmin
};

export default adminApiFunctions;
